import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Shield, Target, Heart, Scale } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const PILLARS = [
  { icon: Target, t: "Education", d: "Plain-language teaching that demystifies a complex system. If you can't understand it, it can't help you." },
  { icon: Shield, t: "Advocacy", d: "Standing beside veterans and equipping them to self-advocate, because informed veterans win claims." },
  { icon: Heart, t: "Empowerment", d: "Confidence, clarity, and ownership of your claims journey. It's your file. You should understand it." },
  { icon: Scale, t: "Integrity", d: "Honest guidance, realistic expectations, no false promises. Outcomes are determined by the VA, always." },
];

export default function About() {
  useSeo({
    title: "About Pamela J. Booker | Veterans Disability Education",
    description: "Pamela J. Booker is a 100% rated U.S. Army veteran teaching fellow veterans how to build strong, evidence-based VA disability claims.",
  });
  return (
    <div>
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="label-caps text-accent mb-5">About VDE</div>
            <h1 className="font-display text-4xl lg:text-6xl leading-[1.05]">
              Founded by a veteran<br />who walked <span className="text-accent">the same road.</span>
            </h1>
            <p className="mt-7 text-lg text-primary-foreground/90 max-w-2xl">
              Veterans Disability Education is a veteran-to-veteran practice. Real strategy. Real evidence. No corporate distance.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1200px] px-5 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Portrait — smaller, sticky on desktop so it scrolls with the copy */}
          <div className="lg:col-span-4">
            <div className="max-w-[320px] mx-auto lg:mx-0 lg:sticky lg:top-24">
              <div className="aspect-[3/4] bg-secondary border-4 border-accent overflow-hidden" data-testid="about-portrait-wrap">
                <img
                  src="/veterans-disability-education-pamela-j-booker-founder.jpg"
                  alt="Pamela J. Booker, founder of Veterans Disability Education — 100% rated U.S. Army veteran"
                  className="w-full h-full object-cover object-top"
                  data-testid="about-portrait"
                />
              </div>
              <div className="mt-4 label-caps text-primary/70">Pamela J. Booker</div>
              <div className="text-sm text-primary/70">Founder, U.S. Army Veteran</div>
            </div>
          </div>

          {/* Copy */}
          <div className="lg:col-span-8">
            <div className="label-caps mb-4">Meet Pamela J. Booker</div>
            <h2 className="font-display text-3xl lg:text-5xl mb-6" data-testid="about-meet-heading">
              A 100% rated Army veteran teaching veterans how the VA actually works.
            </h2>
            <div className="space-y-5 text-lg leading-relaxed text-primary/90">
              <p>I served in the United States Army. I filed my own VA disability claim, learned the hard way what the system actually requires, and eventually earned a 100% rating.</p>
              <p>Along the way I watched other veterans get denied, lowballed, or bled dry by predatory claims consultants charging thousands upfront or taking a cut of backpay. All for work the veteran could have done themselves, with the right education.</p>
              <p>That's why I built Veterans Disability Education. I've helped 20+ veterans get service connected or reach 100%. Not by filing their claims, that's an accredited VSO's job, but by teaching them how to build claims on the evidence the VA actually needs.</p>
              <p data-testid="about-healthcare-line">Sometimes the win isn't a big rating — it's getting a veteran service-connected at all, so the door to VA healthcare finally opens. Several of my clients started exactly there.</p>
              <p className="text-primary font-semibold">Real strategy, not guesswork. <span className="text-accent-foreground/90">Educated. Advocated. Compensated.</span></p>
              <p>Ready to see what your rating could be worth? Start with the calculator, then come talk to me.</p>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" data-testid="about-cta-calc">
                <Link to="/calculator">Try the calculator <ArrowRight className="w-4 h-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" data-testid="about-cta-intake">
                <Link to="/intake">Start your free consult</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20">
          <div className="max-w-3xl mb-12">
            <div className="label-caps mb-4">Our brand pillars</div>
            <h2 className="font-display text-3xl lg:text-5xl">Four ideas we won't compromise on.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p, i) => (
              <div key={i} className="bg-white p-8 border-t-4 border-accent" data-testid={`pillar-${i}`}>
                <p.icon className="w-8 h-8 text-primary mb-5" strokeWidth={1.7} />
                <h3 className="font-display text-xl mb-3">{p.t}</h3>
                <p className="text-[15px] text-primary/80 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20 text-center">
          <div className="gold-rule mx-auto mb-6"></div>
          <div className="label-caps text-accent mb-3">Educated. Advocated. Compensated.</div>
          <h2 className="font-display text-3xl lg:text-5xl max-w-3xl mx-auto">Real strategy, not guesswork.</h2>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" data-testid="about-footer-cta-intake">
              <Link to="/intake">Start Your Free Consult <ArrowRight className="w-4 h-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary">
              <Link to="/calculator">Try the calculator first</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
